import { memo, useCallback, useEffect, useState } from 'react';

import { DEBOUNCE_MS } from '@/components/features/UsersList/constants';
import BaseMultiAutocomplete from '@/components/shared/Autocomplete/BaseMultiAutocomplete';
import { BaseMultiAutocompleteProps } from '@/components/shared/Autocomplete/types';
import type { UserAutocompleteResult } from '@/hooks/autocomplete/useUsersAutocomplete';
import { useUsersAutocomplete } from '@/hooks/autocomplete/useUsersAutocomplete';

export interface UserAutocompleteProps {
  organizationId: number;
  value: UserAutocompleteResult[];
  onChange: BaseMultiAutocompleteProps<UserAutocompleteResult>['autocomplete']['onChange'];
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  enabled?: boolean;
  width?: string | number;
}

function UserAutocomplete({
  organizationId,
  value,
  onChange,
  label = 'Filter by User',
  placeholder = 'Enter at least 3 characters to search',
  disabled = false,
  enabled = true,
  width,
}: UserAutocompleteProps) {
  const [inputValue, setInputValue] = useState('');
  const [debouncedValue, setDebouncedValue] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(inputValue), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [inputValue]);

  const { data, isLoading } = useUsersAutocomplete({
    organizationId,
    filter: debouncedValue,
    enabled: enabled && !disabled,
  });

  const handleInputChange = useCallback((_: React.SyntheticEvent, input: string) => {
    setInputValue(input);
  }, []);

  const getOptionLabel = useCallback((option: UserAutocompleteResult) => option.email ?? '', []);

  const getOptionDisabled = useCallback((option: UserAutocompleteResult) => value.some((v) => v.email === option.email), [value]);

  return (
    <BaseMultiAutocomplete<UserAutocompleteResult>
      autocomplete={{
        options: data ?? [],
        value,
        inputValue,
        onInputChange: handleInputChange,
        onChange,
        getOptionLabel,
        getOptionDisabled,
        filterOptions: (x) => x,
        label,
        placeholder,
        loading: isLoading,
        disabled,
        width,
      }}
    />
  );
}

export default memo(UserAutocomplete);
